import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import ArchitectureBackground from './ArchitectureBackground';
import './PlatformArchitecture.css';

const MOBILE_BREAKPOINT = 768;

const PlatformArchitecture = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const { language } = useLanguage();
  const t = getTranslation(language);

  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTrustworthy, setActiveTrustworthy] = useState(null);
  const [activeAgent, setActiveAgent] = useState(null);

  // Slide index (0=Intelligence, 1=Trustworthy, 2=Agent)
  const [slide, setSlide] = useState(0);
  const scrollContainerRef = useRef(null);

  // Detect mobile to skip scroll-pinning
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const computeSlide = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const scrollable = el.offsetHeight - vh;
    if (scrollable <= 0) return;

    const scrolled = -rect.top;
    if (scrolled <= 0) {
      setSlide((s) => (s !== 0 ? 0 : s));
      return;
    }
    if (scrolled >= scrollable) {
      setSlide((s) => (s !== 2 ? 2 : s));
      return;
    }

    const progress = scrolled / scrollable;
    let next;
    if (progress < 0.34) next = 0;
    else if (progress < 0.67) next = 1;
    else next = 2;
    setSlide((s) => (s !== next ? next : s));
  }, []);

  useEffect(() => {
    if (isMobile) return; // no scroll-pin on mobile
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(computeSlide);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    computeSlide();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [computeSlide, isMobile]);

  const scrollToSlide = (idx) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const scrollable = el.offsetHeight - window.innerHeight;
    const centerProgress = (idx + 0.5) / 3;
    const target = window.scrollY + rect.top + centerProgress * scrollable;
    window.scrollTo({ top: target, behavior: 'smooth' });
  };

  /* ----------------------------------------------------------------------
     Geometric icon (unchanged from original — small SVG marks per category)
     ---------------------------------------------------------------------- */
  const GeoIcon = ({ type }) => {
    const colors = { from: '#C9A96E', to: '#9A7B4F', accent: '#DFC08A' };
    if (type === 'hexagon') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoHex" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <polygon points="14,2 25,8 25,20 14,26 3,20 3,8" fill="none" stroke="url(#geoHex)" strokeWidth="2"/>
        <circle cx="14" cy="14" r="4" fill={colors.accent} opacity="0.6"/>
      </svg>
    );
    if (type === 'diamond') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoDia" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <rect x="6" y="6" width="7" height="7" rx="1.5" fill="url(#geoDia)" opacity="0.7" transform="rotate(45 9.5 9.5)"/>
        <rect x="15" y="6" width="7" height="7" rx="1.5" fill="url(#geoDia)" opacity="0.5" transform="rotate(45 18.5 9.5)"/>
        <rect x="6" y="15" width="7" height="7" rx="1.5" fill="url(#geoDia)" opacity="0.5" transform="rotate(45 9.5 18.5)"/>
        <rect x="15" y="15" width="7" height="7" rx="1.5" fill="url(#geoDia)" opacity="0.7" transform="rotate(45 18.5 18.5)"/>
      </svg>
    );
    if (type === 'circle') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoCir" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <circle cx="14" cy="14" r="11" fill="none" stroke="url(#geoCir)" strokeWidth="2"/>
        <circle cx="10" cy="12" r="2.5" fill={colors.accent} opacity="0.6"/>
        <circle cx="18" cy="12" r="2.5" fill={colors.accent} opacity="0.6"/>
        <path d="M9 18 Q14 22 19 18" fill="none" stroke={colors.from} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
    if (type === 'triangle') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoTri" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <polygon points="14,3 26,24 2,24" fill="none" stroke="url(#geoTri)" strokeWidth="2" strokeLinejoin="round"/>
        <circle cx="14" cy="17" r="3" fill={colors.accent} opacity="0.5"/>
      </svg>
    );
    if (type === 'shield') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoShi" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <path d="M14 2 L24 7 L24 16 Q24 24 14 26 Q4 24 4 16 L4 7 Z" fill="none" stroke="url(#geoShi)" strokeWidth="2"/>
        <path d="M10 14 L13 17 L18 11" fill="none" stroke={colors.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
    if (type === 'lock') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoLok" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <rect x="6" y="13" width="16" height="12" rx="2" fill="none" stroke="url(#geoLok)" strokeWidth="2"/>
        <path d="M10 13 V9 Q10 4 14 4 Q18 4 18 9 V13" fill="none" stroke={colors.accent} strokeWidth="2"/>
        <circle cx="14" cy="19" r="2" fill={colors.from}/>
      </svg>
    );
    if (type === 'star') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoSta" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <polygon points="14,2 17,10 26,10 19,16 21,25 14,20 7,25 9,16 2,10 11,10" fill="none" stroke="url(#geoSta)" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="14" cy="13" r="3" fill={colors.accent} opacity="0.5"/>
      </svg>
    );
    if (type === 'gear') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoGea" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <circle cx="14" cy="14" r="5" fill="none" stroke="url(#geoGea)" strokeWidth="2"/>
        <circle cx="14" cy="14" r="9" fill="none" stroke={colors.accent} strokeWidth="1" strokeDasharray="4 3" opacity="0.6"/>
        <circle cx="14" cy="14" r="2" fill={colors.from} opacity="0.7"/>
      </svg>
    );
    if (type === 'network') return (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <defs><linearGradient id="geoNet" x1="0" y1="0" x2="28" y2="28"><stop offset="0%" stopColor={colors.from}/><stop offset="100%" stopColor={colors.to}/></linearGradient></defs>
        <line x1="14" y1="6" x2="6" y2="22" stroke={colors.accent} strokeWidth="1" opacity="0.5"/>
        <line x1="14" y1="6" x2="22" y2="22" stroke={colors.accent} strokeWidth="1" opacity="0.5"/>
        <line x1="6" y1="22" x2="22" y2="22" stroke={colors.accent} strokeWidth="1" opacity="0.5"/>
        <circle cx="14" cy="6" r="3" fill="none" stroke="url(#geoNet)" strokeWidth="1.5"/>
        <circle cx="6" cy="22" r="3" fill="none" stroke="url(#geoNet)" strokeWidth="1.5"/>
        <circle cx="22" cy="22" r="3" fill="none" stroke="url(#geoNet)" strokeWidth="1.5"/>
      </svg>
    );
    return null;
  };

  /* ----------------------------------------------------------------------
     Per-layer data
     ---------------------------------------------------------------------- */
  const intelligenceCategories = [
    { id: 'recommender', title: t.platform.intelligence.recommender.title, items: t.platform.intelligence.recommender.items, iconType: 'hexagon' },
    { id: 'web',         title: t.platform.intelligence.web.title,         items: t.platform.intelligence.web.items,         iconType: 'diamond' },
    { id: 'customer',    title: t.platform.intelligence.customer.title,    items: t.platform.intelligence.customer.items,    iconType: 'circle'  },
  ];
  const trustworthyCategories = [
    { id: 'explainability', title: t.platform.trustworthy.explainability.title, items: t.platform.trustworthy.explainability.items, iconType: 'triangle' },
    { id: 'robustness',     title: t.platform.trustworthy.robustness.title,     items: t.platform.trustworthy.robustness.items,     iconType: 'shield'   },
    { id: 'privacy',        title: t.platform.trustworthy.privacy.title,        items: t.platform.trustworthy.privacy.items,        iconType: 'lock'     },
  ];
  const agentCategories = [
    { id: 'core',          title: t.platform.agent.core.title,          items: t.platform.agent.core.items,          iconType: 'star'    },
    { id: 'tooling',       title: t.platform.agent.tooling.title,       items: t.platform.agent.tooling.items,       iconType: 'gear'    },
    { id: 'orchestration', title: t.platform.agent.orchestration.title, items: t.platform.agent.orchestration.items, iconType: 'network' },
  ];

  // Scattered illustrations per category. The tree displays text labels;
  // the images live independently on the OPPOSITE side of the right-zone
  // (LTR layers: tree text on left, images scattered on right;
  //  RTL layer:  tree text on right, images scattered on left).
  // Three-tier (hero / medium / small) for visual depth.
  const intelligenceImagesByCategory = {
    recommender: [
      { src: '/intelligence/recommender/context-aware.png',  alt: 'Context-Aware Models',      size: 245, top: '28%', left: '54%', opacity: 0.98, tier: 'hero' },
      { src: '/intelligence/recommender/deep-learning.png',  alt: 'Deep Learning',             size: 160, top: '0%',  left: '66%', opacity: 0.9,  tier: 'medium' },
      { src: '/intelligence/recommender/rl.png',             alt: 'Reinforcement Learning',    size: 185, top: '64%', left: '68%', opacity: 0.92, tier: 'medium' },
      { src: '/intelligence/recommender/hybrid.png',         alt: 'Hybrid Architectures',      size: 110, top: '4%',  left: '88%', opacity: 0.7,  tier: 'small' },
      { src: '/intelligence/recommender/multimodal.png',     alt: 'Multimodal Recommendation', size: 120, top: '80%', left: '52%', opacity: 0.75, tier: 'small' },
    ],
    web: [
      { src: '/intelligence/web/knowledge-graph.png',     alt: 'Knowledge Graphs',      size: 250, top: '28%', left: '56%', opacity: 0.98, tier: 'hero' },
      { src: '/intelligence/web/data-analytics.png',      alt: 'Data Analytics',        size: 170, top: '1%',  left: '65%', opacity: 0.9,  tier: 'medium' },
      { src: '/intelligence/web/gnn.png',                 alt: 'Graph Neural Networks', size: 175, top: '62%', left: '66%', opacity: 0.92, tier: 'medium' },
      { src: '/intelligence/web/plugins.png',             alt: 'Third-party Plug-ins',  size: 115, top: '2%',  left: '88%', opacity: 0.7,  tier: 'small' },
      { src: '/intelligence/web/iterative-retrieval.png', alt: 'Iterative Retrieval',   size: 125, top: '76%', left: '52%', opacity: 0.75, tier: 'small' },
    ],
    customer: [
      { src: '/intelligence/customer/multimodal-llm.png',      alt: 'Multimodal & Personalized LLMs', size: 255, top: '28%', left: '56%', opacity: 0.98, tier: 'hero' },
      { src: '/intelligence/customer/insight-driven.png',      alt: 'Insight-Driven Service',         size: 175, top: '2%',  left: '66%', opacity: 0.9,  tier: 'medium' },
      { src: '/intelligence/customer/emotion-aware.png',       alt: 'Emotion-Aware AI',               size: 180, top: '62%', left: '66%', opacity: 0.92, tier: 'medium' },
      { src: '/intelligence/customer/continuous-learning.png', alt: 'Continuous Learning',            size: 118, top: '4%',  left: '88%', opacity: 0.72, tier: 'small' },
    ],
  };

  const trustworthyImagesByCategory = {
    explainability: [
      { src: '/trustworthy/explainability/xai-framework.png', alt: 'XAI Framework',     size: 240, top: '28%', left: '4%',  opacity: 0.98, tier: 'hero' },
      { src: '/trustworthy/explainability/watermarking.png',  alt: 'Model Watermarking', size: 165, top: '0%',  left: '26%', opacity: 0.9,  tier: 'medium' },
      { src: '/trustworthy/explainability/opa.png',           alt: 'Open Policy Agent', size: 165, top: '64%', left: '26%', opacity: 0.92, tier: 'medium' },
    ],
    robustness: [
      { src: '/trustworthy/robustness/firewall.png',            alt: 'AI Firewalls',          size: 240, top: '28%', left: '4%',  opacity: 0.98, tier: 'hero' },
      { src: '/trustworthy/robustness/formal-verification.png', alt: 'Formal Verification',   size: 165, top: '1%',  left: '26%', opacity: 0.9,  tier: 'medium' },
      { src: '/trustworthy/robustness/adversarial.png',         alt: 'Adversarial Debiasing', size: 175, top: '64%', left: '26%', opacity: 0.92, tier: 'medium' },
      { src: '/trustworthy/robustness/data-lineage.png',        alt: 'Data Lineage',          size: 115, top: '4%',  left: '40%', opacity: 0.7,  tier: 'small' },
    ],
    privacy: [
      { src: '/trustworthy/privacy/federated.png',             alt: 'Federated Learning',     size: 240, top: '28%', left: '4%',  opacity: 0.98, tier: 'hero' },
      { src: '/trustworthy/privacy/zkp.png',                   alt: 'Zero-Knowledge Proof',   size: 165, top: '0%',  left: '26%', opacity: 0.9,  tier: 'medium' },
      { src: '/trustworthy/privacy/homomorphic.png',           alt: 'Homomorphic Encryption', size: 175, top: '64%', left: '26%', opacity: 0.92, tier: 'medium' },
      { src: '/trustworthy/privacy/differential-privacy.png',  alt: 'Differential Privacy',   size: 115, top: '78%', left: '40%', opacity: 0.72, tier: 'small' },
    ],
  };

  const agentImagesByCategory = {
    core: [
      { src: '/agent/core/hierarchical-reasoning.png', alt: 'Hierarchical Reasoning', size: 245, top: '28%', left: '54%', opacity: 0.98, tier: 'hero' },
      { src: '/agent/core/memory.png',                 alt: 'Memory Management',      size: 160, top: '0%',  left: '66%', opacity: 0.9,  tier: 'medium' },
      { src: '/agent/core/react.png',                  alt: 'ReAct',                  size: 185, top: '64%', left: '68%', opacity: 0.92, tier: 'medium' },
      { src: '/agent/core/reflexion.png',              alt: 'Reflexion',              size: 110, top: '4%',  left: '88%', opacity: 0.7,  tier: 'small' },
      { src: '/agent/core/planning.png',               alt: 'Planning',               size: 120, top: '80%', left: '52%', opacity: 0.75, tier: 'small' },
    ],
    tooling: [
      { src: '/agent/tooling/tool-integration.png',  alt: 'Tool Integration',       size: 250, top: '28%', left: '56%', opacity: 0.98, tier: 'hero' },
      { src: '/agent/tooling/mcp.png',               alt: 'Model Context Protocol', size: 170, top: '1%',  left: '65%', opacity: 0.9,  tier: 'medium' },
      { src: '/agent/tooling/task-automation.png',   alt: 'Task Automation',        size: 175, top: '62%', left: '66%', opacity: 0.92, tier: 'medium' },
      { src: '/agent/tooling/exception-recovery.png', alt: 'Exception Recovery',    size: 115, top: '2%',  left: '88%', opacity: 0.7,  tier: 'small' },
    ],
    orchestration: [
      { src: '/agent/orchestration/multi-agent.png',       alt: 'Multi-Agent Collaboration',  size: 255, top: '28%', left: '56%', opacity: 0.98, tier: 'hero' },
      { src: '/agent/orchestration/workflow.png',          alt: 'Workflow Orchestration',     size: 175, top: '2%',  left: '65%', opacity: 0.9,  tier: 'medium' },
      { src: '/agent/orchestration/inter-agent-comm.png',  alt: 'Inter-Agent Communication',  size: 180, top: '62%', left: '66%', opacity: 0.92, tier: 'medium' },
      { src: '/agent/orchestration/monitoring.png',        alt: 'Data Drift & Model Ops Monitoring', size: 120, top: '4%', left: '88%', opacity: 0.72, tier: 'small' },
    ],
  };

  /* ----------------------------------------------------------------------
     Tree renderer — branches fan out from the hovered button to each leaf.
     Each leaf is just dot + text (no image). Images are scattered separately
     on the OPPOSITE side of the zone via renderScatteredIllustrations.
     direction: 'ltr' = root on left (Intelligence / Agent),
                'rtl' = root on right (Trustworthy)
     ---------------------------------------------------------------------- */
  const renderTree = (cats, activeId, direction = 'ltr') => {
    const cat = cats.find((c) => c.id === activeId);
    const items = cat?.items || [];
    const n = items.length;
    const isRtl = direction === 'rtl';

    // y positions: evenly distributed across 10-90% of zone height
    const ys = Array.from({ length: n }, (_, i) =>
      n === 1 ? 50 : 10 + (i / (n - 1)) * 80
    );

    // Root y tracks the hovered button
    const buttonIdx = cats.findIndex((c) => c.id === activeId);
    const nButtons = cats.length;
    const rootY =
      nButtons <= 1 ? 50 : 15 + (buttonIdx / (nButtons - 1)) * 70;

    // Branches occupy a narrower strip so the rest of the zone is free
    // for the scattered illustrations.
    const rootX = isRtl ? 100 : 0;
    const markerX = isRtl ? 82 : 18;
    const ctrlX1 = isRtl ? rootX - 14 : rootX + 14;
    const ctrlX2 = (rootX + markerX) / 2;

    return (
      <AnimatePresence mode="wait">
        {activeId && n > 0 && (
          <motion.div
            key={activeId}
            className={`intelligence-tree intelligence-tree-${direction}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <svg
              className="tree-branches"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {ys.map((y, i) => (
                <motion.path
                  key={i}
                  d={`M ${rootX} ${rootY} C ${ctrlX1} ${rootY}, ${ctrlX2} ${y}, ${markerX} ${y}`}
                  stroke="#C9A96E"
                  strokeOpacity={0.6}
                  strokeWidth={1.2}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{
                    duration: 0.55,
                    delay: i * 0.13,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                />
              ))}
            </svg>

            {ys.map((y, i) => {
              const baseDelay = i * 0.13 + 0.45;
              const posStyle = isRtl
                ? { top: `${y}%`, right: `${100 - markerX}%` }
                : { top: `${y}%`, left: `${markerX}%` };
              return (
                <div
                  key={`leaf-${i}`}
                  className={`tree-leaf tree-leaf-${direction}`}
                  style={posStyle}
                >
                  <motion.span
                    className="leaf-dot"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      duration: 0.32,
                      delay: baseDelay,
                      ease: [0.175, 0.885, 0.32, 1.275],
                    }}
                  />
                  <motion.span
                    className="leaf-text"
                    initial={{ opacity: 0, x: isRtl ? 14 : -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.35,
                      delay: baseDelay + 0.08,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                  >
                    {items[i]}
                  </motion.span>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // Scattered illustrations — independent absolute positions, no relation to
  // tree branches. Images appear with a small stagger after the tree finishes.
  const renderScatteredIllustrations = (images, activeId) => {
    const order = { small: 0, medium: 1, hero: 2 };
    const ordered = [...images].sort(
      (a, b) => (order[a.tier] ?? 1) - (order[b.tier] ?? 1)
    );
    return (
      <AnimatePresence>
        {activeId && ordered.map((img, idx) => (
          <motion.img
            key={img.alt}
            src={img.src}
            alt={img.alt}
            className={`scattered-illustration tier-${img.tier || 'medium'}`}
            style={{ width: `${img.size}px`, top: img.top, left: img.left }}
            initial={{ opacity: 0, scale: 0.55, y: 14 }}
            animate={{ opacity: img.opacity ?? 0.9, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.55, y: -10 }}
            transition={{
              duration: 0.55,
              delay: 0.4 + idx * 0.08,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          />
        ))}
      </AnimatePresence>
    );
  };

  const renderButtons = (cats, activeId, setActiveId, side = 'left') => (
    <div className="category-buttons" onMouseLeave={() => setActiveId(null)}>
      {cats.map((cat, index) => (
        <motion.div
          key={cat.id}
          className={`category-btn ${activeId === cat.id ? 'active' : ''}`}
          initial={{ opacity: 0, x: side === 'left' ? -60 : 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.15 + index * 0.08, ease: [0.4, 0, 0.2, 1] }}
          onMouseEnter={() => setActiveId(cat.id)}
        >
          <span className="category-icon"><GeoIcon type={cat.iconType} /></span>
          <span className="category-title">{cat.title}</span>
        </motion.div>
      ))}
    </div>
  );

  const hoverHint = (
    <motion.div
      className="hover-hint"
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.4 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="hint-circles"><span></span><span></span><span></span></div>
      <p>{language === 'zh' ? '移动鼠标到按钮探索' : 'Hover over buttons to explore'}</p>
    </motion.div>
  );

  /* ----------------------------------------------------------------------
     Slide content factories — return JSX, used by both desktop & mobile
     ---------------------------------------------------------------------- */
  const intelligenceContent = (
    <>
      <motion.div
        className="intelligence-label"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
      >
        <span className="layer-accent-line"></span>
        <h3>{t.platform.intelligence.title}</h3>
        <p>{t.platform.intelligence.subtitle}</p>
      </motion.div>

      <div className="intelligence-interactive">
        {renderButtons(intelligenceCategories, activeCategory, setActiveCategory, 'left')}
        <div className="intelligence-right-zone">
          {renderScatteredIllustrations(
            intelligenceImagesByCategory[activeCategory] || [],
            activeCategory
          )}
          {renderTree(intelligenceCategories, activeCategory, 'ltr')}
          {!activeCategory && hoverHint}
        </div>
      </div>
    </>
  );

  const trustworthyContent = (
    <>
      <motion.div
        className="intelligence-label intelligence-label-right"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
      >
        <span className="layer-accent-line layer-accent-gray"></span>
        <h3>{t.platform.trustworthy.title}</h3>
        <p>{t.platform.trustworthy.subtitle}</p>
      </motion.div>

      <div className="intelligence-interactive interactive-rtl">
        <div className="intelligence-right-zone">
          {renderScatteredIllustrations(
            trustworthyImagesByCategory[activeTrustworthy] || [],
            activeTrustworthy
          )}
          {renderTree(trustworthyCategories, activeTrustworthy, 'rtl')}
          {!activeTrustworthy && hoverHint}
        </div>
        {renderButtons(trustworthyCategories, activeTrustworthy, setActiveTrustworthy, 'right')}
      </div>
    </>
  );

  const agentContent = (
    <>
      <motion.div
        className="intelligence-label"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
      >
        <span className="layer-accent-line layer-accent-dark"></span>
        <h3>{t.platform.agent.title}</h3>
        <p>{t.platform.agent.subtitle}</p>
      </motion.div>

      <div className="intelligence-interactive">
        {renderButtons(agentCategories, activeAgent, setActiveAgent, 'left')}
        <div className="intelligence-right-zone">
          {renderScatteredIllustrations(
            agentImagesByCategory[activeAgent] || [],
            activeAgent
          )}
          {renderTree(agentCategories, activeAgent, 'ltr')}
          {!activeAgent && hoverHint}
        </div>
      </div>
    </>
  );

  const slideTransition = { duration: 0.45, ease: [0.4, 0, 0.2, 1] };
  const slideMotionProps = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    exit:    { opacity: 0, y: -30 },
    transition: slideTransition,
  };

  return (
    <section id="platform" className="platform-architecture" ref={ref}>
      <div ref={scrollContainerRef} className="platform-scroll-container">
        <div className="platform-sticky">
          <ArchitectureBackground activeLayer={isMobile ? 0 : slide} />

          <div className="platform-stage">
            <motion.div
              className="architecture-header"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="section-title">{t.platform.title}</h2>
              <p className="section-subtitle">{t.platform.subtitle}</p>
            </motion.div>

            <div className="platform-slides">
              {isMobile ? (
                <>
                  <div className="platform-slide platform-slide-stacked">{intelligenceContent}</div>
                  <div className="platform-slide platform-slide-stacked">{trustworthyContent}</div>
                  <div className="platform-slide platform-slide-stacked">{agentContent}</div>
                </>
              ) : (
                <AnimatePresence mode="wait" initial={false}>
                  {slide === 0 && (
                    <motion.div key="intelligence" className="platform-slide" {...slideMotionProps}>
                      {intelligenceContent}
                    </motion.div>
                  )}
                  {slide === 1 && (
                    <motion.div key="trustworthy" className="platform-slide" {...slideMotionProps}>
                      {trustworthyContent}
                    </motion.div>
                  )}
                  {slide === 2 && (
                    <motion.div key="agent" className="platform-slide" {...slideMotionProps}>
                      {agentContent}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>

            {!isMobile && (
              <div className="platform-progress" role="tablist" aria-label="Platform layer">
                {[
                  t.platform.intelligence.title,
                  t.platform.trustworthy.title,
                  t.platform.agent.title,
                ].map((label, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={slide === i}
                    className={`platform-progress-dot ${slide === i ? 'is-active' : ''}`}
                    onClick={() => scrollToSlide(i)}
                    aria-label={label}
                    title={label}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformArchitecture;
